import { Period } from '@elementary/common-ui/types';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { PERIOD_PLURAL } from './constants';

const formatPeriod = (period: Period['period'], count: number): string =>
  pluralizeString(count, ...PERIOD_PLURAL[period]);

export const isValidPeriod = (value: unknown): value is Period =>
  typeof value === 'object' &&
  value !== null &&
  'count' in value &&
  'period' in value &&
  typeof (value as Period).count === 'number' &&
  typeof (value as Period).period === 'string' &&
  // hasOwnProperty, not `in`: `in` matches inherited names like 'toString',
  // whose lookup returns a function that formatPeriod would then try to spread.
  Object.prototype.hasOwnProperty.call(PERIOD_PLURAL, (value as Period).period);

export const formatThreshold = (threshold: Period | null | undefined): string =>
  isValidPeriod(threshold)
    ? formatPeriod(threshold.period, threshold.count)
    : 'None';

const formatWeeklyTimeRange = (
  range: Record<string, unknown>,
): string | null => {
  const start = range.start;
  const end = range.end;
  if (
    typeof start === 'object' &&
    start !== null &&
    typeof end === 'object' &&
    end !== null
  ) {
    const startDay = (start as { day_of_week?: unknown }).day_of_week;
    const startTime = (start as { time_of_day?: unknown }).time_of_day;
    const endDay = (end as { day_of_week?: unknown }).day_of_week;
    const endTime = (end as { time_of_day?: unknown }).time_of_day;
    if (
      typeof startDay === 'string' &&
      typeof startTime === 'string' &&
      typeof endDay === 'string' &&
      typeof endTime === 'string'
    ) {
      const timezone =
        typeof range.timezone === 'string' ? ` (${range.timezone})` : '';
      return `${startDay} ${startTime} → ${endDay} ${endTime}${timezone}`;
    }
  }

  if (
    typeof range.startDay === 'string' &&
    typeof range.startTime === 'string' &&
    typeof range.endDay === 'string' &&
    typeof range.endTime === 'string'
  ) {
    return `${range.startDay} ${range.startTime} → ${range.endDay} ${range.endTime}`;
  }

  return null;
};

const formatAbsoluteTimeRange = (
  range: Record<string, unknown>,
): string | null => {
  if (
    typeof range.start_time === 'string' &&
    typeof range.end_time === 'string'
  ) {
    return `${range.start_time} → ${range.end_time}`;
  }
  return null;
};

const formatTimeRangeObject = (value: object): string => {
  const range = value as Record<string, unknown>;
  return (
    formatWeeklyTimeRange(range) ??
    formatAbsoluteTimeRange(range) ??
    (() => {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    })()
  );
};

// Renders an arbitrary dbt-test param value (the Parameters column shows
// free-form args). Periods are formatted like "14 days"; arrays are joined;
// other objects fall back to JSON so they never render as "[object Object]".
export const formatParamValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (isValidPeriod(value)) return formatThreshold(value);
  if (Array.isArray(value)) {
    const labels = value.map(formatParamValue).filter(Boolean);
    return labels.length > 0 ? labels.join('; ') : 'None';
  }
  if (typeof value === 'object') {
    return formatTimeRangeObject(value);
  }
  return String(value);
};

export const formatConfigChangeValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'None';
  if (Array.isArray(value) && value.length === 0) return 'None';
  const formatted = formatParamValue(value);
  return formatted || 'None';
};
